import { Avatar, Box } from '@cambridgeassessment/cambridge-ui';
import { Typography } from '@material-ui/core';
import React, { memo, useMemo } from 'react';
import { hot } from 'react-hot-loader/root';
import useOrgAdmins from 'src/hooks/useOrgAdmins';
import { OrgUser } from 'src/types';
import { filterOrgUsers } from 'src/utils/filterOrgUsers';
import { getUserName } from 'src/utils/transformers';
import OrgUserStatus from '../OrgUserStatus/OrgUserStatus';
import Table from '../Table/Table';
import UserStatusDescriptionCard from '../TooltipDescriptionCard/UserStatusDescriptionCard';
import { headCells } from './constants';
import { useStylesTable } from './styles';
import { Columns, Row } from './types';
import { sortColumns } from './utils';

const tooltips = {
  status: <UserStatusDescriptionCard />,
};

export const createRows = (admins: OrgUser[] = []): Row[] =>
  admins.map((admin) => ({
    cells: {
      [Columns.FullName]: (
        <Box
          alignItems="center"
          display="flex"
          data-sortvalue={getUserName(admin)}
        >
          <Box mr={2}>
            <Avatar
              src={admin.profilePicture}
              data-testid="avatar-picture"
              size="xs"
            />
          </Box>
          <Typography variant="body1" data-testid="userName-typography">
            {getUserName(admin)}
          </Typography>
        </Box>
      ),
      [Columns.Status]: (
        <OrgUserStatus status={admin.status} data-sortvalue={admin.status} />
      ),
    },
    link: `/organisation/users/${admin.UID}`,
  }));

const OrgAdminTable: React.FC = () => {
  const {
    admins,
    isLoading,
    error,
    searchQuery,
    tableSettings,
    updateTableSettings,
  } = useOrgAdmins();

  const orgAdmins = filterOrgUsers(searchQuery, admins);

  const rows = useMemo(() => createRows(orgAdmins), [orgAdmins]);

  const classes = useStylesTable();
  const noResultsMessage = useMemo(
    () => (
      <Box>
        <Typography variant="body1">
          <Box display="flex" justifyContent="center">
            No results were found for&nbsp;
            <Typography variant="subtitle1">{searchQuery}</Typography>.
          </Box>
        </Typography>
        <Typography variant="body1">
          Check your spelling or try searching with another keyword.
        </Typography>
      </Box>
    ),
    [searchQuery]
  );

  return (
    <Table
      loading={isLoading}
      error={!!error}
      noResultsMessage={noResultsMessage}
      headCells={headCells}
      rows={rows}
      settings={tableSettings}
      sortColumns={sortColumns}
      updateSettings={updateTableSettings}
      data-testid="orgAdmin-table"
      tooltips={tooltips}
      className={classes.table}
    />
  );
};

export default hot(memo(OrgAdminTable));
